import React, { useEffect, useState, useRef } from 'react';
import '../realestate.css';
import locationAreaInfo from '../data/locationareainfo.json';
import { buildApiUrl } from '../utils/apiUrl';
import trackVisitor from '../utils/visitorTracker';

const placeholderImg = '/defaultimages/imagenotavailable.jpg';

const getListingPhotos = (item) => {
  const rawPhotos = item?.photos;
  let parsedPhotos = [];

  if (Array.isArray(rawPhotos)) {
    parsedPhotos = rawPhotos;
  } else if (typeof rawPhotos === 'string' && rawPhotos.trim()) {
    const trimmed = rawPhotos.trim();

    if (trimmed.startsWith('[')) {
      try {
        const jsonPhotos = JSON.parse(trimmed);
        parsedPhotos = Array.isArray(jsonPhotos) ? jsonPhotos : [trimmed];
      } catch {
        parsedPhotos = [trimmed];
      }
    } else if (trimmed.includes(',')) {
      parsedPhotos = trimmed.split(',').map(photo => photo.trim());
    } else {
      parsedPhotos = [trimmed];
    }
  }

  const photosWithPrimary = [item?.imagepath, ...parsedPhotos]
    .filter(photo => typeof photo === 'string' && photo.trim())
    .map(photo => photo.trim());

  return Array.from(new Set(photosWithPrimary));
};

const RealEstate = () => {
  const [listings, setListings] = useState([]);
  const [search, setSearch] = useState('');
  const [locationname, setLocationname] = useState('');
  const [areaName, setAreaName] = useState('');
  const [businesstype, setbusinessType] = useState('');
  const [modalItem, setModalItem] = useState(null);
  const [selectedModalImage, setSelectedModalImage] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showToast] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);

  // Ref for horizontally scrolling featured row
  const featuredScrollRef = useRef(null);

  const handleViewDetails = (item) => {
    try {
      const bidVal = item.bid || item.slno || item.id || item.businessname || '';
      trackVisitor('realestate', String(bidVal), 'Real estate listing clicked');
    } catch (e) {
      console.log('Tracking error', e);
    }

    const photos = getListingPhotos(item);
    setModalItem({ ...item, photos });
    setSelectedModalImage(photos[0] || placeholderImg);
  };
  // Only one closeModal definition needed
  const closeModal = () => {
    setModalItem(null);
    setSelectedModalImage(null);
  };

  useEffect(() => {
    const fetchListings = async () => {
      try {
        const res = await fetch(buildApiUrl('/api/realestate'));
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!Array.isArray(data)) {
          throw new Error('Invalid response format');
        }

        // Keep fields consistent with earlier normalization
        const normalized = data.map(d => ({
          ...d,
          businessname: d.businessname || d.premisesname || '',
          propertyprice: d.propertyprice || d.price || '',
          locationname: d.locationname || d.location || '',
          areaname: d.areaname || d.areaName || '',
          businesstype: d.businesstype || d.type || '',
          total_bedrooms: d.total_bedrooms ?? d.beds ?? '',
          total_bathrooms: d.total_bathrooms ?? d.baths ?? '',
          address1: d.address1 || d.address || '',
          contactnumber: d.contactnumber || d.contactno || '',
          aboutdescription: d.aboutdescription || d.description || '',
          website: d.website || '',
          photos: getListingPhotos(d),
        }));

        setListings(normalized);
      } catch (err) {
        console.error('Error fetching real estate:', err);
      }
    };

    fetchListings();
  }, []);

  // Get unique city and type lists for dropdowns (using transformed API field names)

  // Get premise name suggestions based on search input
  const suggestions = search.length > 0
    ? Array.from(new Set(
      listings
        .filter(item => (item.businessname || '').toLowerCase().includes(search.toLowerCase()))
        .map(item => item.businessname)
        .filter(Boolean)
      )).slice(0, 8)
    : Array.from(new Set(listings.map(item => item.businessname).filter(Boolean))).slice(0, 8);

  // Helpers for phone and share text





  // compute filter options from shared location/area master data (same as generalservices)
  const locationOptions = (locationAreaInfo?.locationNames || []).slice().sort((a, b) => {
    const orderA = Number.isFinite(Number(a.listorder)) ? Number(a.listorder) : Number.MAX_SAFE_INTEGER;
    const orderB = Number.isFinite(Number(b.listorder)) ? Number(b.listorder) : Number.MAX_SAFE_INTEGER;
    return orderA - orderB || String(a.label || '').localeCompare(String(b.label || ''));
  });
  const areaOptions = Array.from(new Set(
    (locationAreaInfo?.areaNames || []).map(areaItem => areaItem.label)
  )).filter(Boolean).sort((a, b) => String(a).localeCompare(String(b)));
  const businessTypeOptions = Array.from(new Set(listings.map(i => i.businesstype || '').filter(Boolean))).sort();

  let filteredListings = listings;
  if (locationname !== '') filteredListings = filteredListings.filter(l => l.locationname === locationname);
  if (areaName !== '') filteredListings = filteredListings.filter(l => l.areaname === areaName || l.areaName === areaName);
  if (businesstype !== '') filteredListings = filteredListings.filter(l => l.businesstype === businesstype);
  if (search.trim() !== '') {
    filteredListings = filteredListings.filter(l =>
      (l.businessname && l.businessname.toLowerCase().includes(search.trim().toLowerCase())) ||
      (l.aboutdescription && l.aboutdescription.toLowerCase().includes(search.trim().toLowerCase()))
    );
  }


  return (
    <div style={{ width: '100%', maxWidth: '100vw', margin: 0, padding: 2, boxSizing: 'border-box', overflowX: 'hidden' }}>
       {/* Header */}
      <header style={{ background: '#e8f5e9', color: '#388e3c', padding: '0.25rem', textAlign: 'center', borderRadius: 8, marginBottom: 2 }}>
        <h1 style={{ margin: 0, fontSize: '1.0rem', fontWeight: 700 }}>Real Estate </h1>
      </header>
      {/* Featured Section (inline, similar to eventplanning) */}
      {listings.filter(i => i.featuredlisting === 1 || i.featuredlisting === true).length > 0 && (
        <div id="featured-section" style={{ marginBottom: '0.2rem', position: 'relative' }}>
          <button
            aria-label="Scroll featured left"
            onClick={() => featuredScrollRef.current?.scrollBy({ left: -250, behavior: 'smooth' })}
            style={{
              position: 'absolute',
              top: '40%',
              left: 0,
              zIndex: 2,
              width: 28,
              height: 28,
              border: '2px solid #1976d2',
              background: '#fff',
              color: '#1976d2',
              borderRadius: '50%',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 18,
              boxShadow: '0 2px 8px rgba(0,0,0,0.10)'
            }}
          >
            <span className="material-icons">chevron_left</span>
          </button>

          <div ref={featuredScrollRef} className="featured-scroller">
            <div style={{ display: 'flex', gap: 0 }}>
              {listings.filter(i => i.featuredlisting === 1 || i.featuredlisting === true).slice(0, 6).map(item => (
                <div key={item.bid || item.slno} className="featured-card" onClick={() => handleViewDetails(item)}>
                  <img src={item.imagepath || placeholderImg} alt={item.businessname} onError={(e) => { e.target.onerror = null; e.target.src = placeholderImg; }} style={{ border: '0.5px solid #e0e0e0', borderRadius: 8 }} />
                  <div style={{ fontSize: 13, color: '#1976d2', fontWeight: 600 }}>{item.businessname}</div>
                  <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>{item.locationname} • {item.areaname}</div>
                </div>
              ))}
            </div>
          </div>

          <button
            aria-label="Scroll featured right"
            onClick={() => featuredScrollRef.current?.scrollBy({ left: 250, behavior: 'smooth' })}
            style={{
              position: 'absolute',
              top: '40%',
              right: 0,
              zIndex: 2,
              width: 28,
              height: 28,
              border: '2px solid #1976d2',
              background: '#fff',
              color: '#1976d2',
              borderRadius: '50%',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 18,
              boxShadow: '0 2px 8px rgba(0,0,0,0.10)'
            }}
          >
            <span className="material-icons">chevron_right</span>
          </button>
        </div>
      )}

      {/* Filters (search + selects) */}
      <div style={{ marginBottom: '1rem', marginTop: '0.2rem', display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ position: 'relative', width: '70%' }}>
          <input type="text" placeholder="Search by name..." value={search} onChange={e => setSearch(e.target.value)} onFocus={() => setShowSuggestions(true)} onBlur={() => setTimeout(() => setShowSuggestions(false), 200)} style={{ width: '100%', padding: '8px 12px', fontSize: 14, border: '1px solid #ccc', borderRadius: 8, boxSizing: 'border-box' }} />
          {showSuggestions && suggestions.length > 0 && (
            <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #ccc', borderTop: 'none', borderRadius: '0 0 8px 8px', maxHeight: 200, overflowY: 'auto', zIndex: 10, boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
              {suggestions.map((name, idx) => (
                <div key={idx} onMouseDown={(e) => { e.preventDefault(); setSearch(name); setShowSuggestions(false); }} style={{ padding: '10px 12px', cursor: 'pointer', fontSize: 14, borderBottom: idx < suggestions.length - 1 ? '1px solid #f0f0f0' : 'none', background: '#fff' }}>{name}</div>
              ))}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <select value={locationname} onChange={e => setLocationname(e.target.value)} style={{ flex: 1, minWidth: 65, padding: '6px', fontSize: 12, border: '1px solid #ccc', borderRadius: 6 }}>
            <option value="">All Locations</option>
            {locationOptions.map(loc => <option key={loc.id || loc.value || loc.label} value={loc.label}>{loc.label}</option>)}
          </select>

          <select value={areaName} onChange={e => setAreaName(e.target.value)} style={{ flex: 1, minWidth: 65, padding: '6px', fontSize: 12, border: '1px solid #ccc', borderRadius: 6 }}>
            <option value="">All Areas</option>
            {areaOptions.map(ar => <option key={ar} value={ar}>{ar}</option>)}
          </select>

          <select value={businesstype} onChange={e => setbusinessType(e.target.value)} style={{ flex: 1, minWidth: 65, padding: '6px', fontSize: 12, border: '1px solid #ccc', borderRadius: 6 }}>
            <option value="">All Types</option>
            {businessTypeOptions.map(tp => <option key={tp} value={tp}>{tp}</option>)}
          </select>

          {/* Featured toggle removed as requested (always showing featured scroller). */}

          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
            <button onClick={() => { setSearch(''); setLocationname(''); setAreaName(''); setbusinessType(''); }} style={{ background: 'none', border: 'none', color: '#1976d2', textDecoration: 'underline', cursor: 'pointer', fontSize: 13 }}>Clear all</button>
            <div style={{ fontSize: 14, color: '#666' }}>{filteredListings.length} {filteredListings.length === 1 ? 'listing' : 'listings'}</div>
          </div>
        </div>
      </div>

      {/* Listings Grid */}
      {filteredListings.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#999' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🔍</div>
          <div style={{ fontSize: 18, marginBottom: 8 }}>No listings found</div>
          <div style={{ fontSize: 14 }}>Try adjusting your search or filters</div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: 0, marginBottom: 20 }}>
          {filteredListings.map(item => (
            <div key={item.bid || item.slno || item.businessname} onClick={() => handleViewDetails(item)} style={{ background: '#fff', border: '0.25px solid #e0e0e0', borderRadius: 12, padding: 0, marginBottom: 0, cursor: 'pointer', transition: 'transform 0.2s, box-shadow 0.2s' }} onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 6px 12px rgba(0,0,0,0.12)'; }} onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.08)'; }}>
              <img src={item.imagepath || placeholderImg} alt={item.businessname} style={{ width: '100%', height: 200, objectFit: 'cover', borderRadius: 8, marginBottom: 0 }} onError={(e) => { e.target.onerror = null; e.target.src = placeholderImg; }} />
              <h3 style={{ margin: '2px 0', fontSize: 14, color: '#1976d2' }}>{item.businessname}</h3>
              {item.businesstype && <div style={{ fontSize: 13, color: '#666', marginBottom: 4, fontWeight: 500 }}>{item.businesstype}</div>}
              <div style={{ fontSize: 12, color: '#888', marginBottom: 6, fontWeight: 600 }}>{item.locationname} • {item.areaname}</div>
              <p style={{ margin: '0 0 8px 0', fontSize: 14, color: '#555', lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{item.aboutdescription}</p>
              {(item.sm_instagram || item.sm_facebook || item.sm_youtube) && (
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  {item.sm_instagram && <a href={`https://instagram.com/${item.sm_instagram}`} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: 5, background: 'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)', color: '#fff', fontSize: 15, fontWeight: 'bold', textDecoration: 'none', fontFamily: 'Arial, sans-serif' }}>IG</a>}
                  {item.sm_facebook && <a href={`https://facebook.com/${item.sm_facebook}`} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: 5, background: '#1877F2', color: '#fff', fontSize: 16, fontWeight: 'bold', textDecoration: 'none', fontFamily: 'Arial, sans-serif' }}>f</a>}
                  {item.sm_youtube && <a href={`https://youtube.com/${item.sm_youtube}`} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: 5, background: '#FF0000', color: '#fff', fontSize: 14, fontWeight: 'bold', textDecoration: 'none', fontFamily: 'Arial, sans-serif' }}>▶</a>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {modalItem && (
        <div
          onClick={closeModal}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: 20
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff',
              borderRadius: 16,
              maxWidth: 450,
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              position: 'relative',
              boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
            }}
          >
            {/* Close button */}
            <button
              onClick={closeModal}
              style={{
                position: 'absolute',
                top: 12,
                right: 12,
                background: 'rgba(0,0,0,0.6)',
                color: '#fff',
                border: 'none',
                borderRadius: '50%',
                width: 32,
                height: 32,
                cursor: 'pointer',
                fontSize: 20,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1
              }}
            >
              ×
            </button>

            {/* Modal content: Preview and Thumbnails */}
            <div style={{ textAlign: 'center', marginTop: 12 }}>
              <img
                src={selectedModalImage || placeholderImg}
                alt={modalItem.businessname}
                style={{ width: '90%', maxWidth: 400, height: 250, objectFit: 'cover', borderRadius: 12, marginBottom: 10, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
                onError={(e) => { e.target.onerror = null; e.target.src = placeholderImg; }}
              />
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, justifyContent: 'center', marginTop: 8 }}>
                {(modalItem.photos && modalItem.photos.length > 0
                  ? modalItem.photos
                  : [modalItem.imagepath || placeholderImg]
                ).map((imgUrl, idx) => (
                  <img
                    key={idx}
                    src={imgUrl || placeholderImg}
                    alt={modalItem.businessname + ' preview ' + (idx + 1)}
                    style={{ width: '60px', height: '40px', objectFit: 'cover', borderRadius: 6, border: selectedModalImage === imgUrl ? '2px solid #1976d2' : '2px solid #eee', cursor: 'pointer', boxShadow: selectedModalImage === imgUrl ? '0 0 6px #1976d2' : 'none' }}
                    onClick={() => setSelectedModalImage(imgUrl)}
                    onError={(e) => { e.target.onerror = null; e.target.src = placeholderImg; }}
                  />
                ))}
              </div>
            </div>

            <div style={{ padding: '8px 20px 20px 20px' }}>
              {(modalItem.featuredlisting === 1 || modalItem.featuredlisting === true) && (
                <div style={{ fontSize: 10, color: '#fff', background: '#7700ffff', padding: '4px 10px', borderRadius: 4, display: 'inline-block', marginBottom: 6, fontWeight: 600 }}>
                  Featured
                </div>
              )}

              <h2 style={{ margin: '0 0 2px 0', fontSize: 18, color: '#1976d2' }}>{modalItem.businessname}</h2>
              {modalItem.businesstype && (
                <div style={{ fontSize: 12, color: '#888', margin: '0 0 4px 0', fontWeight: 700 }}>{modalItem.businesstype}</div>
              )}
              <div style={{ fontSize: 14, color: '#888', marginBottom: 2, fontWeight: 600 }}>
                📍 {modalItem.locationname} • {modalItem.areaname}
              </div>

              <div style={{ marginTop: 4, marginBottom: 4 }}>
                  <strong style={{ fontSize: '0.90rem', color: '#333', display: 'inline' }}>Description:</strong>
                  <span style={{ marginLeft: 6, color: '#333', lineHeight: 1.4, fontSize: '0.90rem', display: 'inline' }}>{modalItem.aboutdescription}</span>
                </div>
              {modalItem.contactnumber && (
                <div style={{ marginBottom: 6 }}>
                  <strong style={{ fontSize: 15, color: '#333' }}>Contact Details:</strong>
                  {modalItem.contactperson && (
                    <div style={{ fontSize: 13, fontWeight: 800, color: '#666', marginTop: 4 }}>
                      Name: <span style={{ fontWeight: 500, color: '#555' }}>{modalItem.contactperson}</span>
                    </div>
                  )}
                  <a 
                    href={`tel:${(modalItem.contactnumber || '').replace(/\s/g, '')}`}
                    style={{ 
                      fontSize: 13, 
                      color: '#d32f2f', 
                      marginTop: 4, 
                      fontWeight: 600,
                      textDecoration: 'none',
                      display: 'block',
                      cursor: 'pointer'
                    }}
                  >
                    📞 {modalItem.contactnumber}
                  </a>
                </div>
              )}

              {modalItem.emailid && (
                <div style={{ marginBottom: 2 }}>
                  <strong style={{ fontSize: 14, color: '#333' }}>Email:</strong>
                  <a href={`mailto:${modalItem.emailid}`} style={{ fontSize: 14, color: '#1976d2', textDecoration: 'none', marginLeft: 8 }}>{modalItem.emailid}</a>
                </div>
              )}


              {(modalItem.address1) && (
                <>
                  {modalItem.address1 && (
                    <div style={{ marginBottom: 4 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <strong style={{ fontSize: 14, color: '#333' }}>Address:</strong>
                        <span style={{ fontSize: 14, color: '#666' }}>
                          {modalItem.address1}
                          {modalItem.address1 && (
                            <>
                              {' '}
                              <a
                                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(modalItem.address1)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: '#1976d2', textDecoration: 'underline', marginLeft: 6, fontSize: 13 }}
                              >
                                [Map]
                              </a>
                            </>
                          )}
                        </span>
                      </div>
                    </div>
                  )}
				</>
              )}

                {modalItem.website && (
                  <div style={{ fontSize: 13, fontWeight: 800, color: '#666', margin: '4px 0 12px 0' }}>
                    Website: <a
                      href={modalItem.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ fontWeight: 400, color: '#1976d2', textDecoration: 'underline', wordBreak: 'break-all' }}
                    >
                      {modalItem.website}
                    </a>
                  </div>
                )}
              {/* Action buttons (match Services modal) */}
              <div style={{ display: 'flex', gap: 4, marginTop: 10, marginBottom: 0, justifyContent: 'center', flexWrap: 'wrap' }}>
                {/* Call Now button */}
                {modalItem.contactnumber && (
                  <button
                    onClick={() => window.location.href = `tel:${(modalItem.contactnumber || '').replace(/\s/g, '')}`}
                    style={{
                      padding: '6px 8px',
                      background: '#4CAF50',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 3,
                      fontSize: 11,
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 2,
                      transition: 'background 0.2s',
                      minHeight: '28px'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#45a049'}
                    onMouseLeave={(e) => e.currentTarget.style.background = '#4CAF50'}
                  >
                    📞 Call
                  </button>
                )}

                {/* Share button with dropdown */}
                <div style={{ position: 'relative' }}>
                  <button
                    onClick={() => setShowShareMenu(!showShareMenu)}
                    style={{
                      padding: '6px 8px',
                      background: '#1976d2',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 3,
                      fontSize: 11,
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 2,
                      transition: 'background 0.2s',
                      minHeight: '28px'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#1565c0'}
                    onMouseLeave={(e) => e.currentTarget.style.background = '#1976d2'}
                  >
                    🔗 Share
                  </button>

                  {/* Share dropdown menu */}
                  {showShareMenu && (
                    <div style={{
                      position: 'absolute',
                      bottom: '100%',
                      left: 0,
                      right: 0,
                      marginBottom: 8,
                      background: '#fff',
                      border: '1px solid #ddd',
                      borderRadius: 8,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      zIndex: 100,
                      overflow: 'hidden'
                    }}>
                      {/* WhatsApp option (default) */}
                      <button
                        onClick={() => {
                          const shareText = `*${modalItem.businessname || modalItem.title}*\n${modalItem.businesstype || ''}\n📍 ${modalItem.locationname || ''}${modalItem.areaname ? ` • ${modalItem.areaname}` : ''}\n📞 ${modalItem.contactnumber || ''}${modalItem.emailid ? `\n✉️ ${modalItem.emailid}` : ''}`;
                          const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
                          window.open(whatsappUrl, '_blank');
                          setShowShareMenu(false);
                        }}
                        style={{
                          width: '100%',
                          padding: '10px 16px',
                          background: '#25D366',
                          color: '#fff',
                          border: 'none',
                          borderBottom: '1px solid #eee',
                          fontSize: 24,
                          fontWeight: 500,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          textAlign: 'center',
                          transition: 'background 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#20ba5a'}
                        onMouseLeave={(e) => e.currentTarget.style.background = '#25D366'}
                      >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                        </svg>
                      </button>

                      {/* Email option */}
                      <button
                        onClick={() => {
                          const subject = encodeURIComponent(`Check out ${modalItem.businessname || modalItem.title}`);
                          const body = encodeURIComponent(
                            `${modalItem.businessname || modalItem.title}\n${modalItem.businesstype || ''}\n\nLocation: ${modalItem.locationname || ''}${modalItem.areaname ? ` • ${modalItem.areaname}` : ''}\n${modalItem.address1 ? `Address: ${modalItem.address1}\n` : ''}Contact: ${modalItem.contactnumber || ''}\n${modalItem.emailid ? `Email: ${modalItem.emailid}\n` : ''}${modalItem.aboutdescription ? `\nAbout:\n${modalItem.aboutdescription}` : ''}`
                          );
                          window.location.href = `mailto:?subject=${subject}&body=${body}`;
                          setShowShareMenu(false);
                        }}
                        style={{
                          width: '100%',
                          padding: '10px 16px',
                          background: '#fff',
                          color: '#333',
                          border: 'none',
                          fontSize: 24,
                          fontWeight: 500,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          textAlign: 'center',
                          transition: 'background 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#f5f5f5'}
                        onMouseLeave={(e) => e.currentTarget.style.background = '#fff'}
                      >
                        ✉️
                      </button>
                    </div>
                  )}
                </div>

                {/* Social media links */}
                {(modalItem.sm_instagram || modalItem.sm_facebook || modalItem.sm_youtube) && (
                  <div style={{ marginTop: 6, paddingTop: 4, borderTop: '1px solid #e0e0e0', width: '100%' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'center' }}>
                      <strong style={{ fontSize: 12, color: '#333' }}>Follow us:</strong>
                      <div style={{ display: 'flex', gap: 6 }}>
                        {modalItem.sm_instagram && (
                          <a
                            href={`https://instagram.com/${modalItem.sm_instagram}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ 
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: 26,
                              height: 26,
                              borderRadius: 5,
                              background: 'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)',
                              color: '#fff',
                              fontSize: 11,
                              fontWeight: 'bold',
                              textDecoration: 'none',
                              fontFamily: 'Arial, sans-serif',
                              transition: 'transform 0.2s'
                            }}
                            title="Instagram"
                            onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                            onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                          >
                            IG
                          </a>
                        )}
                        {modalItem.sm_facebook && (
                          <a
                            href={`https://facebook.com/${modalItem.sm_facebook}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ 
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: 26,
                              height: 26,
                              borderRadius: 5,
                              background: '#1877F2',
                              color: '#fff',
                              fontSize: 11,
                              fontWeight: 'bold',
                              textDecoration: 'none',
                              fontFamily: 'Arial, sans-serif',
                              transition: 'transform 0.2s'
                            }}
                            title="Facebook"
                            onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                            onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                          >
                            f
                          </a>
                        )}
                        {modalItem.sm_youtube && (
                          <a
                            href={`https://youtube.com/${modalItem.sm_youtube}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ 
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: 26,
                              height: 26,
                              borderRadius: 5,
                              background: '#FF0000',
                              color: '#fff',
                              fontSize: 11,
                              fontWeight: 'bold',
                              textDecoration: 'none',
                              fontFamily: 'Arial, sans-serif',
                              transition: 'transform 0.2s'
                            }}
                            title="YouTube"
                            onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                            onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                          >
                            ▶
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {showToast && (
        <div style={{ position: 'fixed', bottom: 20, left: '50%', transform: 'translateX(-50%)', background: '#333', color: '#fff', padding: '12px 24px', borderRadius: 6, fontSize: '0.95rem', zIndex: 10000, boxShadow: '0 2px 10px rgba(0,0,0,0.3)' }}>Link copied to clipboard!</div>
      )}
    </div>
  );
};

export default RealEstate;
